import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  CpuIcon as Cpu,
  ListIcon as List,
  SignOutIcon as SignOut,
  UserCircleIcon as UserCircle,
  XIcon as X,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { navItemsFor } from "@/lib/navItems";

export default function TopNav() {
  const { user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef(null);

  // Iter 54 — the menu never survives a navigation, and Escape always closes it.
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header
      className="sticky top-0 z-30 border-b border-[var(--ml-border)] bg-white/85 backdrop-blur-xl"
      data-testid="top-nav"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="brand-link">
          <div className="w-7 h-7 bg-[var(--ml-primary)] flex items-center justify-center shrink-0">
            <Cpu size={18} color="#fff" weight="bold" />
          </div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-semibold tracking-tight text-[15px] whitespace-nowrap truncate">Modernization Lab</span>
            <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hidden sm:inline">
              v1.0
            </span>
          </div>
        </Link>

        {/* Desktop (≥ lg): inline navigation — unchanged markup. */}
        <div className="hidden lg:flex items-center gap-2" data-testid="desktop-nav">
          {user ? (
            <>
              <nav className="flex items-center gap-1" aria-label="Main">
                {navItemsFor(user).map((item) => (
                  <NavItem key={item.key} to={item.to} testid={`nav-${item.key}`} label={item.label} />
                ))}
              </nav>
              <UserMenu />
            </>
          ) : user === null ? (
            <nav className="flex items-center gap-1" aria-label="Main">
              <NavItem to="/framework" testid="nav-framework" label="Framework" />
              <NavLink
                to="/login"
                data-testid="nav-login"
                className="ml-2 px-3 py-1.5 text-sm font-semibold text-[var(--ml-primary)] hover:bg-[var(--ml-surface)] border border-[var(--ml-primary)] transition-colors"
              >
                Sign in
              </NavLink>
              <NavLink
                to="/register"
                data-testid="nav-register"
                className="px-3 py-1.5 text-sm font-semibold text-white bg-[var(--ml-primary)] hover:bg-[var(--ml-primary-hover)] transition-colors"
              >
                Sign up
              </NavLink>
            </nav>
          ) : null}
        </div>

        {/* Phone / tablet (< lg): one button, menu panel below the bar. */}
        {user !== undefined && (
          <button
            type="button"
            ref={triggerRef}
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 border border-transparent hover:border-[var(--ml-border)] hover:bg-[var(--ml-surface)] transition-colors"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            data-testid="mobile-menu-trigger"
          >
            {menuOpen ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        )}
      </div>

      {menuOpen && <MobileMenu user={user} onClose={closeMenu} />}
    </header>
  );
}

function MobileMenu({ user, onClose }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const firstLinkRef = useRef(null);

  useEffect(() => { firstLinkRef.current?.focus(); }, []);

  const onLogout = async () => {
    onClose();
    await logout();
    navigate("/login", { replace: true });
  };

  const linkClass = ({ isActive }) =>
    `block px-4 py-3 text-sm font-medium border-l-2 transition-colors ${
      isActive
        ? "border-[var(--ml-primary)] text-[var(--ml-primary)] bg-[var(--ml-surface)]"
        : "border-transparent text-[var(--ml-text-primary)] hover:bg-[var(--ml-surface)]"
    }`;
  const items = navItemsFor(user);

  return (
    <>
      <button
        type="button"
        aria-label="Close navigation menu"
        onClick={onClose}
        className="lg:hidden absolute inset-x-0 top-full h-screen bg-black/20 cursor-default"
        data-testid="mobile-nav-backdrop"
        tabIndex={-1}
      />
      <div
        id="mobile-nav"
        role="dialog"
        aria-modal="false"
        aria-label="Navigation menu"
        className="lg:hidden absolute inset-x-0 top-16 bg-white border-b border-[var(--ml-border)] shadow-md max-h-[calc(100vh-4rem)] overflow-y-auto"
        data-testid="mobile-nav"
      >
        <nav aria-label="Main" className="py-2">
          {items.map((item, i) => (
            <NavLink
              key={item.key}
              to={item.to}
              ref={i === 0 ? firstLinkRef : undefined}
              onClick={onClose}
              className={linkClass}
              data-testid={`mobile-nav-${item.key}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        {user ? (
          <div className="border-t border-[var(--ml-border)] px-4 py-3 flex items-center justify-between gap-3" data-testid="mobile-menu-account">
            <div className="min-w-0 flex items-center gap-2">
              <UserCircle size={18} weight="bold" className="text-[var(--ml-primary)] shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-semibold truncate" data-testid="mobile-menu-email">{user.name || user.email}</div>
                <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{user.role}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="ml-btn-secondary text-xs shrink-0 flex items-center gap-1.5"
              data-testid="mobile-menu-logout"
            >
              <SignOut size={14} weight="bold" /> Sign out
            </button>
          </div>
        ) : (
          <div className="border-t border-[var(--ml-border)] px-4 py-3 flex gap-2">
            <NavLink to="/login" onClick={onClose} data-testid="mobile-nav-login"
                     className="flex-1 text-center px-3 py-2 text-sm font-semibold text-[var(--ml-primary)] border border-[var(--ml-primary)]">
              Sign in
            </NavLink>
            <NavLink to="/register" onClick={onClose} data-testid="mobile-nav-register"
                     className="flex-1 text-center px-3 py-2 text-sm font-semibold text-white bg-[var(--ml-primary)]">
              Sign up
            </NavLink>
          </div>
        )}
      </div>
    </>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="relative ml-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="user-menu-trigger"
        className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-[var(--ml-surface)] border border-transparent hover:border-[var(--ml-border)] transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <UserCircle size={18} weight="bold" className="text-[var(--ml-primary)]" />
        <span className="text-sm font-medium hidden md:inline" data-testid="user-menu-name">
          {user.name || user.email}
        </span>
      </button>
      {open && (
        <div
          role="menu"
          data-testid="user-menu-dropdown"
          className="absolute right-0 mt-1 w-56 bg-white border border-[var(--ml-border)] shadow-md"
        >
          <div className="px-3 py-2 border-b border-[var(--ml-border)]">
            <div className="text-xs font-semibold truncate">{user.email}</div>
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mt-0.5">
              {user.role}
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="user-menu-logout"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[var(--ml-surface)] transition-colors"
          >
            <SignOut size={14} weight="bold" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function NavItem({ to, label, testid }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
          isActive
            ? "text-[var(--ml-primary)] bg-[var(--ml-surface)]"
            : "text-[var(--ml-text-primary)] hover:bg-[var(--ml-surface)]"
        }`
      }
      data-testid={testid}
    >
      {label}
    </NavLink>
  );
}
